
import Box from '@mui/material/Box';
import EDTypography from '../Typography/EDTypography';
import {
  IRapportMallControl,
  isRapportTextControl,
  isRapportEditBoxControl,
  isRapportTextBoxControl,
  isRapportCheckBoxControl,
  isRapportImageControl,
  isRapportBoxControl,
  isRapportLineControl,
  isRapportContainerControl,
  isRapportDataSetControl,
  isRapportCustomGridControl,
  isRapportAssociationControl,
  isRapportSpacingControl,
  isRapportAddressWindowControl,
  isRapportNewPageControl,
  isRapportConditionControl,
} from './RapportMallTypes';
import { UpdateControlFunction } from './RapportMall';
import { RapportText } from './RapportText';
import { RapportEditBox } from './RapportEditBox';
import { RapportTextBox } from './RapportTextBox';
import { RapportCheckBox } from './RapportCheckBox';
import { RapportImage } from './RapportImage';
import { RapportBox } from './RapportBox';
import { RapportLine } from './RapportLine';
import { RapportContainer } from './RapportContainer';
import { RapportDataSet } from './RapportDataSet';
import { RapportCustomGrid } from './RapportCustomGrid';
import { RapportAssociation } from './RapportAssociation';
import { RapportSpacing } from './RapportSpacing';
import { RapportAddressWindow } from './RapportAddressWindow';
import { RapportNewPage } from './RapportNewPage';
import { RapportCondition } from './RapportCondition';
import { HOVER_SHADOW, SELECTED_COLOR } from './RapportMallUtils';

export interface IProps {
  ctrl: IRapportMallControl;
  idx: number;
  sx?: any;
  maxWidth: number;
  defaultHeight: number;
  setChildHovering?: (hovering: boolean) => void;
  onSelect?: (path: number[]) => void;
  onUpdate?: UpdateControlFunction;
  controlPath: number[];
  selectedPath?: number[];
  pageMarginLeft?: number;
  pageMarginTop?: number;
  pageMarginRight?: number;
  pageMarginBottom?: number;
  zIndex?: number;
  showDottedBorders: boolean;
}

export const RapportKontroll: React.FC<IProps> = ({
  ctrl,
  idx,
  sx,
  maxWidth,
  defaultHeight,
  setChildHovering,
  onSelect,
  onUpdate,
  controlPath,
  selectedPath,
  pageMarginLeft,
  pageMarginTop,
  zIndex,
  showDottedBorders,
}) => {
  const hoverSx = {
    transition: 'box-shadow 0.2s, border-color 0.2s',
    '&:hover': {
      boxShadow: HOVER_SHADOW,
    },
    zIndex: zIndex,
  };
  const handleMouseEnter = () => setChildHovering && setChildHovering(true);
  const handleMouseLeave = () => setChildHovering && setChildHovering(false);

  // Funktion för att jämföra paths shallow
  const isSelected =
    selectedPath &&
    controlPath.length === selectedPath.length &&
    controlPath.every((v, i) => v === selectedPath[i]);

  const baseSx = { ...hoverSx, ...sx };

  // Add a dotted outline if enabled and the control does not already show its own decoration
  const dottedSx = (hasDecor: boolean) =>
    showDottedBorders && !hasDecor
      ? {
          outline: `1px dotted ${SELECTED_COLOR}`,
          outlineOffset: '-1px',
        }
      : {};

  const commonProps = {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onSelect,
    onUpdate: onUpdate
      ? (updatedControl: IRapportMallControl) =>
          onUpdate(controlPath, updatedControl)
      : undefined,
    controlPath: controlPath,
    selectedPath: selectedPath,
    selected: isSelected,
    zIndex: zIndex,
    maxWidth: maxWidth,
    defaultHeight: defaultHeight,
  };
  return (
    <>
      {isRapportTextControl(ctrl) ? (
        <RapportText
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportEditBoxControl(ctrl) ? (
        <RapportEditBox
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportTextBoxControl(ctrl) ? (
        <RapportTextBox
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportCheckBoxControl(ctrl) ? (
        <RapportCheckBox
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportImageControl(ctrl) ? (
        <RapportImage
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportBoxControl(ctrl) ? (
        <RapportBox
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportLineControl(ctrl) ? (
        <RapportLine
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportContainerControl(ctrl) ? (
        <RapportContainer
          {...commonProps}
          ctrl={ctrl}
          key={idx}
          sx={{ ...dottedSx(!!isSelected) }}
          onSelect={onSelect}
          onUpdate={onUpdate}
          showDottedBorders={showDottedBorders}
        />
      ) : isRapportDataSetControl(ctrl) ? (
        <RapportDataSet
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportCustomGridControl(ctrl) ? (
        <RapportCustomGrid
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportAssociationControl(ctrl) && controlPath.length === 1 ? (
        <RapportAssociation
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportSpacingControl(ctrl) && controlPath.length === 1 ? (
        <RapportSpacing
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportAddressWindowControl(ctrl) && controlPath.length === 1 ? (
        <RapportAddressWindow
          ctrl={ctrl}
          key={idx}
          {...commonProps}
          pageMarginLeft={pageMarginLeft ?? 0}
          pageMarginTop={pageMarginTop ?? 0}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportNewPageControl(ctrl) && controlPath.length === 1 ? (
        <RapportNewPage
          key={idx}
          {...commonProps}
          sx={{ ...baseSx, ...dottedSx(!!isSelected) }}
        />
      ) : isRapportConditionControl(ctrl) ? (
        <RapportCondition
          {...commonProps}
          ctrl={ctrl}
          key={idx}
          sx={baseSx}
          onSelect={onSelect}
          onUpdate={onUpdate}
          showDottedBorders={showDottedBorders}
        />
      ) : (
        <Box
          key={idx}
          sx={{
            background: 'background.default',
            border: '1px solid #aaa',
            p: 1,
            mb: 1,
            ...baseSx,
            ...dottedSx(true),
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-control-path={controlPath.join('-')}
        >
          {/* fallback för okänd typ */}
          <EDTypography variant="caption" color="textSecondary">
            {'Ohanterad typ: ' + (ctrl.Type || 'Okänd typ')}
          </EDTypography>
        </Box>
      )}
    </>
  );
};
